import { useMemo } from 'react';
import { Formik, Form } from 'formik';
import Yup from 'utils/validation/yup-extensions';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import {
	DepartmentSupplement,
	FORM_FIELD_KEYS as DEPARTMENT_FIELD_KEYS,
	INITIAL_VALUES as DEPARTMENT_INITIAL_VALUES,
	VALIDATION_SCHEMA as DEPARTMENT_VALIDATION_SCHEMA,
} from './DepartmentSupplement';
import {
	SpecialtySupplement,
	FORM_FIELD_KEYS as SPECIALTY_FIELD_KEYS,
	INITIAL_VALUES as SPECIALTY_INITIAL_VALUES,
	VALIDATION_SCHEMA as SPECIALTY_VALIDATION_SCHEMA,
} from './SpecialtySupplement';
import {
	NPISupplement,
	FORM_FIELD_KEYS as NPI_FORM_FIELD_KEYS,
	INITIAL_VALUES as NPI_INITIAL_VALUES,
	VALIDATION_SCHEMA as NPI_VALIDATION_SCHEMA,
} from './NPISupplement';
import {
	ResearchSupplement,
	FORM_FIELD_KEYS as RESEARCH_FORM_FIELD_KEYS,
	INITIAL_VALUES as RESEARCH_INITIAL_VALUES,
	VALIDATION_SCHEMA as RESEARCH_VALIDATION_SCHEMA,
} from './ResearchSupplement';
import {
	BillingSupplement,
	FORM_FIELD_KEYS as BILLING_FORM_FIELD_KEYS,
	INITIAL_VALUES as BILLING_INITIAL_VALUES,
	VALIDATION_SCHEMA as BILLING_VALIDATION_SCHEMA,
} from './BillingSupplement';
import {
	RadiologySupplement,
	FORM_FIELD_KEYS as RADIOLOGY_FORM_FIELD_KEYS,
	INITIAL_VALUES as RADIOLOGY_INITIAL_VALUES,
	VALIDATION_SCHEMA as RADIOLOGY_VALIDATION_SCHEMA,
} from './RadiologySupplement';
import {
	AnesthesiologySupplement,
	FORM_FIELD_KEYS as ANESTHESIOLOGY_FORM_FIELD_KEYS,
	INITIAL_VALUES as ANESTHESIOLOGY_INITIAL_VALUES,
	VALIDATION_SCHEMA as ANESTHESIOLOGY_VALIDATION_SCHEMA,
} from './AnesthesiologySupplement';
import { OBJ_SUPPLEMENTS } from '../../_constants';

export const Supplement = ({
	id,
	lastName,
	firstName,
	open = false,
	type,
	onAccept = () => {},
	onDiscard = () => {},
}) => {
	const formikInitialValues = useMemo(() => {
		return {
			...(type === OBJ_SUPPLEMENTS.DEPARTMENT && {
				...DEPARTMENT_INITIAL_VALUES,
			}),
			...(type === OBJ_SUPPLEMENTS.SPECIALTIES && {
				...SPECIALTY_INITIAL_VALUES,
			}),
			...(type === OBJ_SUPPLEMENTS.NPI && {
				...NPI_INITIAL_VALUES,
				[NPI_FORM_FIELD_KEYS.LAST_NAME]: lastName,
				[NPI_FORM_FIELD_KEYS.FIRST_NAME]: firstName,
			}),
			...(type === OBJ_SUPPLEMENTS.RESEARCH && {
				...RESEARCH_INITIAL_VALUES,
			}),
			...(type === OBJ_SUPPLEMENTS.BILLING && {
				...BILLING_INITIAL_VALUES,
			}),
			...(type === OBJ_SUPPLEMENTS.RADIOLOGY && {
				...RADIOLOGY_INITIAL_VALUES,
			}),
			...(type === OBJ_SUPPLEMENTS.ANESTHESIOLOGY && {
				...ANESTHESIOLOGY_INITIAL_VALUES,
			}),
		};
	}, [type, lastName, firstName]);

	const formikValidationSchema = useMemo(() => {
		return Yup.object().shape({
			...(type === OBJ_SUPPLEMENTS.DEPARTMENT &&
				DEPARTMENT_VALIDATION_SCHEMA),
			...(type === OBJ_SUPPLEMENTS.SPECIALTIES &&
				SPECIALTY_VALIDATION_SCHEMA),
			...(type === OBJ_SUPPLEMENTS.NPI && NPI_VALIDATION_SCHEMA),
			...(type === OBJ_SUPPLEMENTS.RESEARCH &&
				RESEARCH_VALIDATION_SCHEMA),
			...(type === OBJ_SUPPLEMENTS.BILLING && BILLING_VALIDATION_SCHEMA),
			...(type === OBJ_SUPPLEMENTS.RADIOLOGY &&
				RADIOLOGY_VALIDATION_SCHEMA),
			...(type === OBJ_SUPPLEMENTS.ANESTHESIOLOGY &&
				ANESTHESIOLOGY_VALIDATION_SCHEMA),
		});
	}, [type]);

	return (
		<Dialog open={open} onClose={() => onDiscard(id)}>
			<Formik
				enableReinitialize
				initialValues={formikInitialValues}
				validationSchema={formikValidationSchema}
				onSubmit={(values, formikBag) => {
					let payload = {};
					switch (type) {
						case OBJ_SUPPLEMENTS.DEPARTMENT: {
							payload = values[DEPARTMENT_FIELD_KEYS.DEPARTMENTS];
							break;
						}
						case OBJ_SUPPLEMENTS.NPI:
							payload = values[NPI_FORM_FIELD_KEYS.NPI];
							break;
						case OBJ_SUPPLEMENTS.SPECIALTIES: {
							payload = values[SPECIALTY_FIELD_KEYS.SPECIALTIES];
							break;
						}
						case OBJ_SUPPLEMENTS.RESEARCH:
							payload = {
								ResearchTrainingCompleted:
									values[
										RESEARCH_FORM_FIELD_KEYS
											.RESEARCH_CITI_TRAINING_DATE
									],
								ResearchType:
									values[
										RESEARCH_FORM_FIELD_KEYS
											.RESEARCH_STUDY_TYPE
									],
								ResearchID:
									values[
										RESEARCH_FORM_FIELD_KEYS
											.RESEARCH_STUDY_ID
									],
							};
							break;
						case OBJ_SUPPLEMENTS.BILLING:
							payload =
								values[BILLING_FORM_FIELD_KEYS.BILLING_NEEDS];
							break;
						case OBJ_SUPPLEMENTS.RADIOLOGY: {
							payload = {
								vendor: values[
									RADIOLOGY_FORM_FIELD_KEYS.RADIOLOGY_VENDOR
								],
								otherVendor: values[
									RADIOLOGY_FORM_FIELD_KEYS.RADIOLOGY_OTHER
								],
							};
							break;
						}
						case OBJ_SUPPLEMENTS.ANESTHESIOLOGY: {
							payload = {
								vendor: values[
									ANESTHESIOLOGY_FORM_FIELD_KEYS
										.ANESTHESIOLOGY_VENDOR
								],
								otherVendor: values[
									ANESTHESIOLOGY_FORM_FIELD_KEYS
										.ANESTHESIOLOGY_OTHER
								],
							};
							break;
						}
						default:
							break;
					}
					onAccept(id, type, payload);
					formikBag.setSubmitting(false);
				}}
			>
				{props => (
					<Form>
						<DialogTitle>
							Supplemental Information Needed
						</DialogTitle>
						<DialogContent>
							<DialogContentText id='label--supplement--dialog'>
								This job category requires supplemental
								information before it can be assigned to this
								staffer.
							</DialogContentText>
							{type === OBJ_SUPPLEMENTS.DEPARTMENT && (
								<DepartmentSupplement formik={props} />
							)}
							{type === OBJ_SUPPLEMENTS.SPECIALTIES && (
								<SpecialtySupplement formik={props} />
							)}
							{type === OBJ_SUPPLEMENTS.NPI && (
								<NPISupplement
									formik={props}
									lastName={lastName}
									firstName={firstName}
								/>
							)}
							{type === OBJ_SUPPLEMENTS.RESEARCH && (
								<ResearchSupplement formik={props} />
							)}
							{type === OBJ_SUPPLEMENTS.BILLING && (
								<BillingSupplement formik={props} />
							)}
							{type === OBJ_SUPPLEMENTS.RADIOLOGY && (
								<RadiologySupplement formik={props} />
							)}
							{type === OBJ_SUPPLEMENTS.ANESTHESIOLOGY && (
								<AnesthesiologySupplement formik={props} />
							)}
						</DialogContent>
						<DialogActions>
							<Button onClick={() => onDiscard(id)}>
								Cancel
							</Button>
							<Button type='submit'>Complete</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};
